import { useEffect, useState, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';

// Format a date as relative time (e.g., "2 hours ago", "Jan 15")
function formatRelativeDate(dateString) {
  if (!dateString) return '';
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;

  // For older dates, show month/day
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

import {
  fetchSyncObjects,
  fetchSyncObjectFields,
  saveSyncChanges,
  triggerMetadataRefresh,
  triggerFieldSync,
  toggleObjectSync,
  revertObjectSync,
  updatePendingFields,
  discardSyncChanges,
  fetchSyncSettings,
  updateSyncFrequency,
  triggerSyncNow,
  clearAuthRequired,
} from '../store/slices/analyticsSlice';
import { redirectToSalesforceReauth, redirectToHubSpotReauth } from '../api/auth';

// Returns the stable state key for an object, preferring qualified_name to avoid
// cross-source collisions when two data sources share an object name (e.g. "contacts").
const objKey = (obj) => obj.qualified_name || obj.name;

function SyncConfigTab() {
  const dispatch = useDispatch();
  const {
    objects,
    isLoadingObjects,
    objectFields,
    isLoadingFields,
    pendingChanges,
    hasUnsavedChanges,
    isSaving,
    error,
    activeWorkflow,
  } = useSelector((state) => state.analytics.syncConfig);

  const {
    frequency,
    isUpdatingFrequency,
    isTriggeringSyncNow,
    syncNowStatus,
    authRequired,
  } = useSelector((state) => state.analytics.tenantSync);

  const { selectedDataSource } = useSelector((state) => state.dataSources);
  const dataSourceId = selectedDataSource?.data_source_id || null;

  const { activeTenant } = useSelector((state) => state.auth);
  const currentTenantId = activeTenant?.tenant_id || null;

  const [searchQuery, setSearchQuery] = useState('');
  const [showSyncedOnly, setShowSyncedOnly] = useState(false);
  const [expandedObjects, setExpandedObjects] = useState({});
  const [loadingFieldsFor, setLoadingFieldsFor] = useState(null);
  // Local field selections - track which fields are selected for each object
  const [fieldSelections, setFieldSelections] = useState({});

  // Fetch objects when data source changes; reset local UI state
  useEffect(() => {
    dispatch(fetchSyncObjects({ dataSourceId }));
    dispatch(fetchSyncSettings());
    setExpandedObjects({});
    setFieldSelections({});
  }, [dispatch, dataSourceId]);

  const handleFrequencyChange = (newFrequency) => {
    dispatch(updateSyncFrequency(newFrequency));
  };

  // Check if a sync is currently running
  const isSyncRunning = activeWorkflow?.status === 'running' || syncNowStatus === 'already_running';

  // Note: Progress polling and completion handling moved to SyncProgressPanel

  // Initialize field selections when objectFields changes.
  // objectFields is already keyed by qualified name (storeKey), so we use the same key.
  useEffect(() => {
    Object.entries(objectFields).forEach(([storeKey, fields]) => {
      // Always initialize when fields load, even if empty
      // Use undefined check to differentiate "not loaded" from "loaded but empty"
      if (fieldSelections[storeKey] === undefined && fields) {
        const syncedFieldNames = fields
          .filter(f => f.is_synced)
          .map(f => f.name);
        setFieldSelections(prev => ({
          ...prev,
          [storeKey]: syncedFieldNames, // May be empty array, that's OK
        }));
      }
    });
  }, [objectFields]);

  const filteredObjects = useMemo(() => {
    return objects.filter((obj) => {
      if (showSyncedOnly && !obj.is_synced) return false;
      if (searchQuery) {
        return (
          obj.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          obj.label.toLowerCase().includes(searchQuery.toLowerCase())
        );
      }
      return true;
    });
  }, [objects, showSyncedOnly, searchQuery]);

  const getObjectStatus = (obj) => {
    const key = objKey(obj);
    if (pendingChanges.add[key]) return 'pending-add';
    if (pendingChanges.remove.includes(key)) return 'pending-remove';
    return obj.is_synced ? 'synced' : 'not-synced';
  };

  // Shared: topic-column rows (is_topic=true) are DuckDB-only and not selectable for sync
  const selectableFields = (fields) => fields.filter(f => !f.is_topic);

  const toggleExpanded = async (obj) => {
    const key = objKey(obj);
    const isExpanded = expandedObjects[key];

    if (!isExpanded && !objectFields[key]) {
      // Load fields if not already loaded
      setLoadingFieldsFor(key);
      await dispatch(fetchSyncObjectFields({ objectName: obj.name, dataSourceId, storeKey: key }));
      setLoadingFieldsFor(null);
    }

    setExpandedObjects(prev => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const handleObjectCheckbox = async (obj) => {
    const key = objKey(obj);
    const currentStatus = getObjectStatus(obj);
    let fields = objectFields[key] || [];

    // Load fields if not already loaded (needed to select all fields)
    if (fields.length === 0) {
      setLoadingFieldsFor(key);
      const result = await dispatch(fetchSyncObjectFields({ objectName: obj.name, dataSourceId, storeKey: key }));
      setLoadingFieldsFor(null);
      if (result.payload) {
        fields = result.payload.fields || [];
      }
    }

    // Toggle logic depends on whether the object is currently synced in the DB
    if (obj.is_synced) {
      // SYNCED objects toggle between: synced ↔ pending-remove
      if (currentStatus === 'pending-remove') {
        // Cancel the pending removal - restore to synced state
        dispatch(revertObjectSync(key));
        // Restore field selections to synced fields
        const syncedFieldNames = fields.filter(f => f.is_synced).map(f => f.name);
        setFieldSelections(prev => ({
          ...prev,
          [key]: syncedFieldNames.length > 0 ? syncedFieldNames : [],
        }));
      } else {
        // Mark for removal
        dispatch(toggleObjectSync({ objectName: key, config: null }));
        setFieldSelections(prev => ({
          ...prev,
          [key]: [],
        }));
      }
    } else {
      // NOT SYNCED objects toggle between: not-synced ↔ pending-add
      if (currentStatus === 'pending-add') {
        // Cancel the pending add - restore to not-synced state
        dispatch(revertObjectSync(key));
        setFieldSelections(prev => ({
          ...prev,
          [key]: [],
        }));
      } else {
        // Add object to sync with ALL selectable (non-topic) fields selected by default
        const allFieldNames = selectableFields(fields).map(f => f.name);
        dispatch(
          toggleObjectSync({
            objectName: key,
            config: {
              object_name: obj.name,
              sync_mode: 'incremental',
              selected_fields: allFieldNames,
            },
          })
        );
        setFieldSelections(prev => ({
          ...prev,
          [key]: allFieldNames,
        }));
        // Auto-expand to show the selected fields
        setExpandedObjects(prev => ({
          ...prev,
          [key]: true,
        }));
      }
    }
  };

  const handleFieldCheckbox = (obj, fieldName, isChecked) => {
    const key = objKey(obj);
    setFieldSelections(prev => {
      const currentFields = prev[key] || [];
      let newFields;

      if (isChecked) {
        newFields = [...currentFields, fieldName];
      } else {
        newFields = currentFields.filter(f => f !== fieldName);
      }

      // Update pending changes (keyed by qualified name; carries raw objectName + dataSourceId for API)
      dispatch(updatePendingFields({ objectKey: key, objectName: obj.name, dataSourceId, fields: newFields }));

      // Also update object sync status if needed
      if (newFields.length === 0) {
        dispatch(toggleObjectSync({ objectName: key, config: null }));
      } else if (newFields.length > 0 && !pendingChanges.add[key]) {
        dispatch(
          toggleObjectSync({
            objectName: key,
            config: {
              object_name: obj.name,
              sync_mode: 'incremental',
              selected_fields: newFields,
            },
          })
        );
      }

      return {
        ...prev,
        [key]: newFields,
      };
    });
  };

  const selectAllFields = (obj) => {
    const key = objKey(obj);
    const fields = objectFields[key] || [];
    const allFieldNames = selectableFields(fields).map(f => f.name);

    setFieldSelections(prev => ({
      ...prev,
      [key]: allFieldNames,
    }));

    dispatch(updatePendingFields({ objectKey: key, objectName: obj.name, dataSourceId, fields: allFieldNames }));
    dispatch(
      toggleObjectSync({
        objectName: key,
        config: {
          object_name: obj.name,
          sync_mode: 'incremental',
          selected_fields: allFieldNames,
        },
      })
    );
  };

  const deselectAllFields = (obj) => {
    const key = objKey(obj);
    setFieldSelections(prev => ({
      ...prev,
      [key]: [],
    }));

    dispatch(updatePendingFields({ objectKey: key, objectName: obj.name, dataSourceId, fields: [] }));
    dispatch(toggleObjectSync({ objectName: key, config: null }));
  };

  const getObjectCheckboxState = (obj) => {
    const status = getObjectStatus(obj);
    const key = objKey(obj);
    const allFields = objectFields[key] || [];

    // Check if fieldSelections has been initialized for this object
    const selectionsInitialized = fieldSelections[key] !== undefined;
    const selectedFields = fieldSelections[key] || [];

    // If pending-remove, always unchecked
    if (status === 'pending-remove') {
      return 'unchecked';
    }

    // If pending-add, always checked (user explicitly added it)
    if (status === 'pending-add') {
      return 'checked';
    }

    // For synced objects
    if (obj.is_synced) {
      // Fields not loaded yet - trust the synced status from backend
      if (allFields.length === 0) {
        return 'checked';
      }

      // Fields loaded but local state not initialized - derive from is_synced flags
      if (!selectionsInitialized) {
        const syncedCount = allFields.filter(f => f.is_synced && !f.is_topic).length;
        const selectableCount = selectableFields(allFields).length;
        if (syncedCount === 0) return 'unchecked';
        if (syncedCount === selectableCount) return 'checked';
        return 'indeterminate';
      }

      // Local state initialized - use it
      const selectableCount = selectableFields(allFields).length;
      if (selectedFields.length === 0) return 'unchecked';
      if (selectedFields.length === selectableCount) return 'checked';
      return 'indeterminate';
    }

    // Not synced - use local selections
    const selectableCount = selectableFields(allFields).length;
    if (selectedFields.length === 0) return 'unchecked';
    if (selectableCount > 0 && selectedFields.length === selectableCount) return 'checked';
    if (selectedFields.length > 0) return 'indeterminate';
    return 'unchecked';
  };

  const handleSave = async () => {
    await dispatch(saveSyncChanges(dataSourceId));
    dispatch(fetchSyncObjects({ dataSourceId }));
  };

  const handleDiscard = () => {
    dispatch(discardSyncChanges());
    // Reset local field selections to match synced state
    const newSelections = {};
    objects.forEach(obj => {
      const key = objKey(obj);
      const fields = objectFields[key] || [];
      newSelections[key] = fields.filter(f => f.is_synced).map(f => f.name);
    });
    setFieldSelections(newSelections);
  };

  const handleRefresh = () => {
    // Just dispatch - SyncProgressPanel handles the progress display
    dispatch(triggerMetadataRefresh({ dataSourceId }));
  };

  const handleSyncNow = async () => {
    // Save changes first, then trigger full sync (metadata + data)
    await dispatch(saveSyncChanges(dataSourceId));
    dispatch(fetchSyncObjects({ dataSourceId }));
    dispatch(triggerSyncNow(dataSourceId));
  };

  // Calculate summary stats
  const syncedCount = objects.filter((o) => o.is_synced).length;
  const pendingAddCount = Object.keys(pendingChanges.add).length;
  const pendingRemoveCount = pendingChanges.remove.length;
  // Objects that will be active after saving pending changes
  const effectiveSyncCount = syncedCount - pendingRemoveCount + pendingAddCount;

  return (
    <div className="space-y-4">
      {/* Data Source Banner */}
      {selectedDataSource && (
        <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800">
          <span className="font-medium">Configuring:</span>
          <span>{selectedDataSource.name || selectedDataSource.source_type}</span>
          <span className="text-blue-500 text-xs font-mono">({selectedDataSource.source_type})</span>
        </div>
      )}

      {/* Header */}
      <div className="bg-white rounded-lg shadow p-4">
        {/* Sync Settings Row */}
        <div className="flex justify-between items-center mb-4 pb-4 border-b border-gray-200">
          <div className="flex items-center gap-4">
            <label className="text-sm font-medium text-gray-700">Auto-sync frequency:</label>
            <select
              value={frequency}
              onChange={(e) => handleFrequencyChange(e.target.value)}
              disabled={isUpdatingFrequency}
              className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:opacity-50"
            >
              <option value="hourly">Hourly</option>
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
              <option value="disabled">Disabled</option>
            </select>
            {isUpdatingFrequency && (
              <svg className="animate-spin h-4 w-4 text-gray-500" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
            )}
          </div>
        </div>

        {/* Search and Filter Row */}
        <div className="flex justify-between items-center mb-4">
          <div className="flex gap-4 items-center">
            <input
              type="text"
              placeholder="Search objects..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg w-64 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
            <label className="flex items-center gap-2 text-sm text-gray-600">
              <input
                type="checkbox"
                checked={showSyncedOnly}
                onChange={(e) => setShowSyncedOnly(e.target.checked)}
                className="h-4 w-4 text-primary-600 rounded"
              />
              Show synced only
            </label>
          </div>
          <span className="text-sm text-gray-500">
            {syncedCount} of {objects.length} objects synced
          </span>
        </div>

        <div className="flex justify-between items-center">
          <div className="flex gap-2">
            <button
              onClick={handleRefresh}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition text-sm font-medium"
            >
              Refresh Metadata
            </button>
          </div>
          <div className="flex gap-2">
            {hasUnsavedChanges && (
              <>
                <button
                  onClick={handleDiscard}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition text-sm font-medium"
                >
                  Discard
                </button>
                <button
                  onClick={handleSave}
                  disabled={isSaving}
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition text-sm font-medium disabled:opacity-50 flex items-center gap-2"
                >
                  {isSaving && (
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                  )}
                  Save Changes
                </button>
              </>
            )}
            <span title={effectiveSyncCount <= 0 ? 'No objects to sync' : undefined}>
              <button
                onClick={handleSyncNow}
                disabled={effectiveSyncCount <= 0 || isSaving || isTriggeringSyncNow || isSyncRunning}
                className={`px-4 py-2 rounded-lg transition text-sm font-medium flex items-center gap-2 ${
                  effectiveSyncCount <= 0
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : isSyncRunning
                    ? 'bg-yellow-100 text-yellow-700 border border-yellow-300'
                    : 'bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50'
                }`}
              >
                {(isTriggeringSyncNow || isSyncRunning) && (
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                )}
                {isSyncRunning ? 'Syncing...' : 'Sync'}
              </button>
            </span>
          </div>
        </div>
      </div>

      {/* Pending Changes Summary */}
      {hasUnsavedChanges && (
        <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-sm text-yellow-800">
          <strong>Unsaved changes:</strong>{' '}
          {pendingAddCount > 0 && <span>{pendingAddCount} to add</span>}
          {pendingAddCount > 0 && pendingRemoveCount > 0 && <span>, </span>}
          {pendingRemoveCount > 0 && <span>{pendingRemoveCount} to remove</span>}
        </div>
      )}

      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">{error}</div>
      )}

      {/* Object Accordion List */}
      <div className="bg-white rounded-lg shadow">
        <div className="max-h-[600px] overflow-y-auto">
          {isLoadingObjects ? (
            <div className="p-8 text-center text-gray-600">Loading objects...</div>
          ) : filteredObjects.length === 0 ? (
            <div className="p-8 text-center text-gray-600">
              {objects.length === 0 ? (
                <div>
                  <p className="mb-2">No objects available.</p>
                  <p className="text-sm">Click "Refresh Metadata" to load objects from your data source.</p>
                </div>
              ) : (
                'No objects match your search.'
              )}
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {filteredObjects.map((obj) => {
                const key = objKey(obj);
                const status = getObjectStatus(obj);
                const isExpanded = expandedObjects[key];
                const fields = objectFields[key] || [];
                const selectedFields = fieldSelections[key] || [];
                const checkboxState = getObjectCheckboxState(obj);
                const isLoadingThisObject = loadingFieldsFor === key;

                return (
                  <div key={key} className={status.startsWith('pending') ? 'bg-yellow-50' : ''}>
                    {/* Object Row */}
                    <div className="flex items-center px-4 py-3 hover:bg-gray-50">
                      {/* Checkbox */}
                      <input
                        type="checkbox"
                        checked={checkboxState === 'checked'}
                        ref={(el) => {
                          if (el) el.indeterminate = checkboxState === 'indeterminate';
                        }}
                        onChange={() => handleObjectCheckbox(obj)}
                        className="h-4 w-4 text-primary-600 rounded mr-3"
                      />

                      {/* Expand/Collapse Button */}
                      <button
                        onClick={() => toggleExpanded(obj)}
                        className="p-1 hover:bg-gray-200 rounded mr-2"
                      >
                        {isLoadingThisObject ? (
                          <svg className="animate-spin h-4 w-4 text-gray-500" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                          </svg>
                        ) : (
                          <svg
                            className={`w-4 h-4 text-gray-500 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        )}
                      </button>

                      {/* Object Name */}
                      <button
                        onClick={() => toggleExpanded(obj)}
                        className="flex-1 text-left"
                      >
                        <span className="font-medium text-gray-900">{obj.label}</span>
                        <span className="text-xs text-gray-500 font-mono ml-2">{obj.name}</span>
                      </button>

                      {/* Status Badge */}
                      <StatusBadge status={status} />

                      {/* Sync Info: field count, date, records */}
                      <div className="flex items-center gap-4 ml-4 text-sm text-gray-500">
                        <span className="w-20 text-right">
                          {obj.synced_field_count || 0}/{obj.field_count} fields
                        </span>
                        {obj.is_synced && obj.last_sync_at && (
                          <span className="text-xs text-gray-400" title={new Date(obj.last_sync_at).toLocaleString()}>
                            {formatRelativeDate(obj.last_sync_at)}
                          </span>
                        )}
                        {obj.is_synced && obj.record_count != null && (
                          <span className="text-xs text-gray-400">
                            {obj.record_count.toLocaleString()} records
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Expanded Fields */}
                    {isExpanded && (
                      <div className="border-t border-gray-100 bg-gray-50">
                        {/* Field Actions */}
                        <div className="px-4 py-2 border-b border-gray-200 flex gap-4 items-center bg-gray-100">
                          <button
                            onClick={() => selectAllFields(obj)}
                            className="text-xs text-primary-600 hover:text-primary-800 font-medium"
                          >
                            Select All
                          </button>
                          <button
                            onClick={() => deselectAllFields(obj)}
                            className="text-xs text-gray-600 hover:text-gray-800 font-medium"
                          >
                            Deselect All
                          </button>
                          <span className="text-xs text-gray-500">
                            {selectedFields.length} of {selectableFields(fields).length} selected
                          </span>
                        </div>

                        {/* Fields List */}
                        {isLoadingFields ? (
                          <div className="p-4 text-center text-gray-500 text-sm">Loading fields...</div>
                        ) : fields.length === 0 ? (
                          <div className="p-4 text-center text-gray-500 text-sm">No fields available</div>
                        ) : (
                          <div className="max-h-64 overflow-y-auto">
                            {fields.map((field) => {
                              if (field.is_topic) {
                                return (
                                  <div key={field.name} className="flex items-center px-4 py-2 bg-violet-50 opacity-75">
                                    <span className="ml-8 mr-3 w-4" />
                                    <span className="flex-1 text-sm text-violet-600 italic">{field.label || field.name}</span>
                                    <span className="text-xs text-violet-400 font-mono w-48">{field.name}</span>
                                    <span className="text-xs text-violet-400 w-24">topic</span>
                                  </div>
                                );
                              }

                              const isSelected = selectedFields.includes(field.name);
                              const wasPreviouslySynced = field.is_synced;

                              return (
                                <label
                                  key={field.name}
                                  className={`flex items-center px-4 py-2 hover:bg-gray-100 cursor-pointer ${
                                    isSelected ? 'bg-primary-50' : ''
                                  }`}
                                >
                                  <input
                                    type="checkbox"
                                    checked={isSelected}
                                    onChange={(e) => handleFieldCheckbox(obj, field.name, e.target.checked)}
                                    className="h-4 w-4 text-primary-600 rounded ml-8 mr-3"
                                  />
                                  <span className="flex-1 text-sm text-gray-900">{field.label || field.name}</span>
                                  <span className="text-xs text-gray-500 font-mono w-48">{field.name}</span>
                                  <span className="text-xs text-gray-400 w-24">{field.type}</span>
                                  {wasPreviouslySynced && (
                                    <span className="w-2 h-2 bg-blue-500 rounded-full ml-2" title="Previously synced" />
                                  )}
                                </label>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Progress handled by SyncProgressPanel in App layout */}

      {/* Auth Required Modal */}
      {authRequired && (
        <AuthRequiredModal
          authRequired={authRequired}
          tenantId={currentTenantId}
          onClose={() => dispatch(clearAuthRequired())}
        />
      )}
    </div>
  );
}

// Modal for when a data source needs authentication
function AuthRequiredModal({ authRequired, tenantId, onClose }) {
  const { source_type, source_name, auth_action } = authRequired;

  // Get the appropriate icon and colors based on source type
  const sourceConfig = {
    salesforce: {
      icon: (
        <svg className="w-12 h-12 text-blue-500" viewBox="0 0 24 24" fill="currentColor">
          <path d="M15.5 5.5C14.5 4.5 13 4 11.5 4C8 4 5 7 5 10.5C5 14 8 17 11.5 17C13 17 14.5 16.5 15.5 15.5L19 19L20.5 17.5L17 14C18 13 18.5 11.5 18.5 10C18.5 6.5 15.5 4 12 4" />
          <circle cx="11" cy="11" r="6" fill="none" stroke="currentColor" strokeWidth="1.5" />
        </svg>
      ),
      color: 'blue',
      displayName: 'Salesforce',
    },
    hubspot: {
      icon: (
        <svg className="w-12 h-12 text-orange-500" viewBox="0 0 24 24" fill="currentColor">
          <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" strokeWidth="1.5" />
          <text x="12" y="16" textAnchor="middle" fontSize="8" fill="currentColor">HS</text>
        </svg>
      ),
      color: 'orange',
      displayName: 'HubSpot',
    },
    default: {
      icon: (
        <svg className="w-12 h-12 text-gray-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <path strokeLinecap="round" strokeLinejoin="round" d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4" />
        </svg>
      ),
      color: 'gray',
      displayName: 'Data Source',
    },
  };

  const config = sourceConfig[source_type] || sourceConfig.default;
  const isReconnect = auth_action === 'reconnect';

  const handleLogin = () => {
    switch (source_type) {
      case 'salesforce':
        redirectToSalesforceReauth('/analytics');
        break;
      case 'hubspot':
        // Pass tenantId so the callback uses the add_to_tenant path, which
        // updates the existing token rather than creating a new hubspot- tenant.
        redirectToHubSpotReauth('/analytics', tenantId);
        break;
      default:
        console.error('Unknown source type for reauth:', source_type);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
        {/* Header */}
        <div className="flex items-center gap-4 mb-4">
          {config.icon}
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              {isReconnect ? 'Reconnect Required' : 'Connect'} {source_name || config.displayName}
            </h3>
            <p className="text-sm text-gray-500">{config.displayName} Data Source</p>
          </div>
        </div>

        {/* Message */}
        <p className="text-gray-600 mb-6">
          {isReconnect
            ? `Your connection to "${source_name || config.displayName}" has expired or been revoked. Please log in again to continue syncing data.`
            : `You need to connect "${source_name || config.displayName}" before you can sync data.`}
        </p>

        {/* Actions */}
        <div className="flex gap-3 justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition text-sm font-medium"
          >
            Cancel
          </button>
          <button
            onClick={handleLogin}
            className={`px-4 py-2 rounded-lg text-white transition text-sm font-medium bg-${config.color}-600 hover:bg-${config.color}-700`}
            style={{
              backgroundColor: config.color === 'blue' ? '#2563eb' : config.color === 'orange' ? '#ea580c' : '#4b5563',
            }}
          >
            {isReconnect ? 'Reconnect' : 'Connect'} {config.displayName}
          </button>
        </div>
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const styles = {
    synced: 'bg-green-100 text-green-700',
    'not-synced': 'bg-gray-100 text-gray-600',
    'pending-add': 'bg-yellow-100 text-yellow-700',
    'pending-remove': 'bg-red-100 text-red-700',
  };

  const labels = {
    synced: 'Synced',
    'not-synced': 'Not synced',
    'pending-add': 'Pending',
    'pending-remove': 'Removing',
  };

  return (
    <span className={`px-2 py-1 text-xs rounded-full ${styles[status]}`}>{labels[status]}</span>
  );
}

export default SyncConfigTab;
